import { SatelliteGroup, TLEData, SatelliteRecord } from '../types/satellite';
import { parseTLEText, validateTLE } from '../utils/tleParser';
import * as satellite from 'satellite.js';

export class CelestrakService {
  private baseURL = (import.meta as any).env?.DEV 
    ? "/celestrak/NORAD/elements/gp.php" // proxied in dev
    : "https://corsproxy.io/?https://celestrak.org/NORAD/elements/gp.php"; // CORS proxy for production
  
  /**
   * Fetch TLE data for a satellite group from CelesTrak
   */
  async fetchTLEData(group: SatelliteGroup, maxSatellites?: number): Promise<TLEData[]> {
    try {
      const url = `${this.baseURL}?GROUP=${encodeURIComponent(group)}&FORMAT=tle`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const text = await response.text();
      const parsed = parseTLEText(text);
      
      // Limit number of satellites if specified
      const limited = maxSatellites ? parsed.slice(0, maxSatellites) : parsed;
      
      // Validate TLE data
      const validated = limited.filter(tle => {
        const isValid = validateTLE(tle);
        if (!isValid) {
          console.warn(`Invalid TLE data for satellite: ${tle.name}`);
        }
        return isValid;
      });
      
      console.log(`Loaded ${validated.length} satellites from group '${group}'`);
      return validated;
      
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(
        `Failed to load TLEs for group '${group}': ${message}. ` +
        'If this is a CORS error, ensure dev proxy is configured for /celestrak'
      );
    }
  }
  
  /**
   * Convert TLE data to satellite records with satrec objects
   */
  createSatelliteRecords(tleData: TLEData[]): SatelliteRecord[] {
    return tleData
      .map(tle => this.createSatelliteRecord(tle))
      .filter((record): record is SatelliteRecord => record !== null);
  }
  
  /**
   * Create a single satellite record from TLE data
   */
  private createSatelliteRecord(tle: TLEData): SatelliteRecord | null {
    try {
      const satrec = satellite.twoline2satrec(tle.line1, tle.line2);
      return { ...tle, satrec };
    } catch (error) {
      console.warn(`Failed to create satrec for ${tle.name}:`, error);
      return null;
    }
  }
}